
import coursesManifest from '../courses-manifest.json'; 
import type { Course, CourseFrontMatter, Lesson, LessonFrontMatter, CourseManifestEntry, Module } from './types';
import { format, parseISO } from 'date-fns';
import slugify from 'slugify';
import MarkdownIt from 'markdown-it';

const md = new MarkdownIt({ html: true });

function processCourseManifestEntry(entry: CourseManifestEntry): Course | null {
    if (!entry || entry.error || !entry.frontMatter) {
        console.warn(`Skipping invalid course entry from manifest: ${entry?.lang}/${entry?.slug}`, entry?.error);
        return null;
    }

    const fm = entry.frontMatter as CourseFrontMatter;

    // Validate required fields (only title is essential for listing)
    if (!fm.title) {
        console.warn(`Skipping course ${entry.slug} due to missing title.`);
        return null;
    }

    // Skip draft courses
    if (fm.draft) {
        console.log(`Skipping draft course: ${entry?.lang}/${entry?.slug}`);
        return null;
    }

    // All courses are now single-page by default
    let processedModules: Module[] = [];
    const processedLessonsMap: Record<string, Lesson[]> = {};
    let singlePageContent = '';

    // Get the single-page content
    singlePageContent = (entry as CourseManifestEntry & { singlePageContent?: string }).singlePageContent || '';

    // Auto-generate sections from H2 headings in the single-page content
    const sections = extractSectionsFromContent(singlePageContent);

    // Use auto-generated sections or fall back to frontmatter sections if they exist
    const sectionsToUse = sections.length > 0 ? sections : (fm.sections || []);

    processedModules = sectionsToUse.map(section => ({
        id: section.id,
        title: section.title,
        position: section.position,
        lessons: [] // Single-page courses don't have traditional lessons
    })).sort((a, b) => a.position - b.position);

    // Process cover image similar to blog posts
    let coverImage = '';
    if (fm.cover) {
        if (typeof fm.cover === 'string') {
            coverImage = fm.cover;
        } else if (fm.cover.image) {
            coverImage = fm.cover.image;
        }
    }

    // Convert price_cents to a default_price object for compatibility
    // Use language-based currency defaults: fr -> eur, en -> usd, with frontmatter override
    const currency = fm.currency || (entry.lang === 'fr' ? 'eur' : 'usd');

    const default_price = fm.price_cents ? {
        id: `price_${entry.slug}`,
        unit_amount: fm.price_cents,
        currency: currency,
        type: 'one_time' as const, // FIXED: Use const assertion for proper typing
        active: true
    } : null;

    // Process markdown content and handle custom blocks
    let renderedContent = '';
    let faqData = null;
    
    if (entry.content) {
        let markdownContent = entry.content;
        
        // Extract FAQ data from :::faq-accordion::: blocks BEFORE markdown processing
        const faqBlockRegex = /:::faq-accordion\s*\n([\s\S]*?)\n:::/g;
        let faqMatch;
        
        while ((faqMatch = faqBlockRegex.exec(markdownContent)) !== null) {
            const faqContent = faqMatch[1];
            
            // Parse Q:/A: pairs using simple regex
            const qaRegex = /Q:\s*(.*?)\nA:\s*(.*?)(?=\nQ:|$)/gs;
            const qaMatches = Array.from(faqContent.matchAll(qaRegex));
            
            if (qaMatches.length > 0) {
                faqData = qaMatches.map(match => ({
                    question: match[1].trim(),
                    answer: match[2].trim()
                }));
            }
            
            // Replace the :::faq-accordion::: block with a component placeholder
            markdownContent = markdownContent.replace(
                faqMatch[0],
                '<div data-component="faq-accordion" class="faq-component-placeholder"></div>'
            );
        }
        
        // Replace placeholders BEFORE markdown processing to avoid interference
        markdownContent = markdownContent
            .replace(/{{enrollment}}/g, '\n\n<div id="enrollment-placeholder" data-placeholder="enrollment" class="enrollment-placeholder"></div>\n\n')
            .replace(/{{content-list}}/g, '\n\n<div id="content-list-placeholder" data-placeholder="content-list" class="content-list-placeholder"></div>\n\n')
            .replace(/{{single-page-content}}/g, '\n\n<div id="single-page-content-placeholder" data-placeholder="single-page-content" class="single-page-content-placeholder"></div>\n\n');
        
        // Now render the markdown to HTML
        let htmlContent = md.render(markdownContent);
        
        
        renderedContent = htmlContent;
    }

    return {
        slug: entry.slug,
        lang: entry.lang,
        title: fm.title,
        description: fm.description || 'No description available.',
        price_cents: fm.price_cents,
        is_free: fm.is_free || false, // Ensure is_free is always boolean
        published_at: fm.published_at ? format(parseISO(fm.published_at), 'MMMM d, yyyy') : 'Date unknown',
        modules: processedModules, // Use processed modules
        lessons: processedLessonsMap, // Use processed lessons map
        content: renderedContent, // Add the rendered HTML content with placeholders replaced
        default_price, // Add default_price for compatibility with StripePrice
        course_type: 'single-page', // All courses are now single-page
        sections: processedModules.map(mod => ({ id: mod.id, title: mod.title, position: mod.position })), // Auto-generated sections
        singlePageContent, // Add single-page content
        external_source: fm.external_source, // Add external source URL
        faqData, // Add extracted FAQ data
        cover: coverImage // Add cover image
    };
}

export async function fetchCourses(lang: string = 'en'): Promise<Course[]> {
    try {
        const courses = (coursesManifest as any[]) // FIXED: Use any[] to bypass type checking
            .filter(entry => entry.lang === lang && !entry.error)
            .map(processCourseManifestEntry)
            .filter((course): course is Course => course !== null); 

        if (!courses || courses.length === 0) {
            console.warn(`No valid courses found for language '${lang}' in the manifest.`);
            return [];
        }

        return courses;

    } catch (error) {
        console.error('Error fetching courses from manifest:', error);
        return [];
    }
}

export async function fetchCourse(slug: string, lang: string = 'en'): Promise<Course | null> {
    try {
        const entry = (coursesManifest as any[]).find( // FIXED: Use any[] to bypass type checking
            e => e.lang === lang && e.slug === slug && !e.error
        );

        if (!entry) {
            console.warn(`Course not found in manifest for ${lang}/${slug}`);
            return null;
        }

        const courseData = processCourseManifestEntry(entry);

        if (!courseData) {
             console.warn(`Failed to process course manifest entry for ${lang}/${slug}`);
             return null;
        }

        return courseData;

    } catch (error) {
        console.error(`Error fetching course ${lang}/${slug} from manifest:`, error);
        return null;
    }
}

// Helper function to extract sections from H1 headings in markdown content
function extractSectionsFromContent(content: string): Array<{id: string, title: string, position: number}> {
    const h1Regex = /^#\s+(.+)$/gm;
    const sections: Array<{id: string, title: string, position: number}> = [];
    let match;
    let position = 1;

    while ((match = h1Regex.exec(content)) !== null) {
        const title = match[1].trim();
        // Convert title to slug for ID
        const id = slugify(title, { lower: true, strict: true });
        sections.push({
            id,
            title,
            position: position++
        });
    }

    return sections;
}

export async function fetchLesson(
  courseSlug: string,
  moduleSlug: string,
  lessonSlug: string,
  lang: string = "en"
): Promise<string | null> {
  try {
    const filePath = `/content/courses/${lang}/${courseSlug}/${moduleSlug}/${lessonSlug}.md`;
    const response = await fetch(filePath);

    if (!response.ok) {
      console.error(`Failed to fetch lesson: ${filePath}, Status: ${response.status}`);
      throw new Error(`Failed to fetch lesson ${filePath}`);
    }

    const markdown = await response.text();

    return md.render(markdown);

  } catch (error) {
    console.error(`Error fetching or rendering lesson ${lang}/${courseSlug}/${moduleSlug}/${lessonSlug}:`, error);
    return null;
  }
}
